import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { ResioLogo } from '@/assets/ResioLogo';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';

interface ResidentMobileHeaderProps {
  title?: string;
  onMenuPress?: () => void;
  showNotification?: boolean;
  onNotificationPress?: () => void;
  unreadCount?: number;
}

export const ResidentMobileHeader: React.FC<ResidentMobileHeaderProps> = ({
  title,
  onMenuPress,
  showNotification = true,
  onNotificationPress,
  unreadCount = 0,
}) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const handleMenuPress = () => {
    if (onMenuPress) {
      onMenuPress();
    } else {
      // Default: go back
      navigation.goBack();
    }
  };

  const handleNotificationPress = () => {
    if (onNotificationPress) {
      onNotificationPress();
    }
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        {/* Left Side - Logo + Notification */}
        <View style={styles.leftContainer}>
          <View style={styles.logoContainer}>
            <ResioLogo width={25.5} height={34} />
            <View style={styles.resioNameContainer}>
              <Text style={styles.resioName}>RESIO</Text>
            </View>
          </View>
          {showNotification && (
            <TouchableOpacity
              style={styles.notificationButton}
              onPress={handleNotificationPress}
              accessibilityLabel="Notifications"
              accessibilityRole="button"
            >
              <RESIDENTS_SVG_COMPONENTS.BELL width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} />
              {unreadCount > 0 && <View style={styles.unreadDot} />}
            </TouchableOpacity>
          )}
        </View>

        {/* Right Side - Title + Menu */}
        <View style={styles.rightContainer}>
          {title && (
            <Text style={styles.title} numberOfLines={1}>
              {title}
            </Text>
          )}
          <TouchableOpacity
            style={styles.menuButton}
            onPress={handleMenuPress}
            accessibilityLabel="Menu"
            accessibilityRole="button"
          >
            <SVG_COMPONENTS.HAMBURGER {...SVG_DIMENSIONS.HAMBURGER} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  leftContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  resioNameContainer: {
    flexDirection: 'column',
    gap: 10,
  },
  resioName: {
    fontSize: 17,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    letterSpacing: 0.5,
  },
  notificationButton: {
    width: 34,
    height: 34,
    backgroundColor: '#2F2F2F',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadDot: {
    position: 'absolute',
    top: 7,
    right: 7,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FF7629',
  },
  rightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 14,
    fontWeight: '500',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    maxWidth: 120,
  },
  menuButton: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

